/*Global Dependencies*/
import { useEffect, useRef, useState } from "react";
import { Input, Spin } from "antd";
import { map , cloneDeep} from "lodash";
import { useLocation } from "react-router-dom";

/*Local Dependencies*/
import Icon from "@assets/icon_config";
import UpdateTextWithDropdown, { FieldState } from "@pages/member-details/overview/memberDetails/update-text-with-dropdown/UpdateTextWithDropdown";
import HighCostPatientService from "@services/high-cost-patient.service";
import "./notesModal.scss";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import memberDetailsService from "@services/memberDetails.service";
import { appInsights } from "../../../appInsights";
import { useHighCostPatientStore } from "@stores/highCostPatientStore";
import ProgressBarChart from "../../progressBarChart/progressBarChart";
import {formatDisplayDate , parseNumber} from "@utils/helpers";
import { routeMap } from "@utils/constants";
import { ExpenseData } from "../highCostPatientConstant";
import classNames from "classnames";
interface highCostNoteProps {
  title: string;
  displayCount: string;
  color?: string;
  size?: number;
  style?: any;
  displayTextInitial: string;
  countFontSize?: number;
  countColor?: string;
  patient_id: any;
  dropdownData: any;
  rank ?: number;
  refreshDate: string;
  tableData ?:  any;
  setTableData ?: (x: any , y ?: string) => void;
  setNotesModal ?:  (x: any) => void;
  expenseData ?:  any;
  setExpenseData ?:  (x: any) => void;
}
// The value used to split the string to identify if it is Other note.
const otherNoteHash = "$ON$_";

const HighCostNote = ({ title, displayCount, color, size, style = {}, displayTextInitial, countFontSize = 20, countColor = "#E75547", patient_id, dropdownData , rank, refreshDate , tableData , setTableData , setNotesModal , expenseData ,setExpenseData }: highCostNoteProps) => {
  const [noteLoadingState, setNoteLoadingState] = useState<boolean | undefined>(undefined);
  const [showOtherInputText, setShowOtherInputText] = useState<boolean>(false);
  const [otherTextValue, setOtherTextValue] = useState<string>("");
  const [displayText, setDisplayText] = useState<any>("");
  const [otherNoteSelected, setOtherNoteSelected] = useState<boolean>(false);
  const defaultFieldState = useRef<FieldState.Dropdown | FieldState.Text>(FieldState.Text);
  const location: any = useLocation();

  useEffect(() => {
    console.log("HighCostNote.useEffect1")
    if (displayTextInitial) {
      // If selected note is Other note.
      if (displayTextInitial.includes(otherNoteHash)) {
        setOtherNoteSelected(true);
        setDisplayText(displayTextInitial.split(otherNoteHash)[1]);
        setOtherTextValue(displayTextInitial.split(otherNoteHash)[1]);
      } else {
        setOtherNoteSelected(false);
        setDisplayText(displayTextInitial);
      }
    }

    if (displayTextInitial === null || displayTextInitial === undefined) {
      setOtherNoteSelected(false);
      setDisplayText(null);
    }
  }, [displayTextInitial]);

  // On Dropdown Select Change
  const handleSelectChange = (value: string) => {
    console.log("HighCostNote.handleSelectChange")
    if (value === "Other") {
      setOtherNoteSelected(true);
      setShowOtherInputText(true);
    } else {
      setOtherNoteSelected(false);
      setDisplayText(value);
      // Create Note
      if (displayText === null) {
        createNote(value);
      }
      // Update Note
      if (displayText !== null) {
        updateNote(value);
      }
    }
  };

  // On Text Input Value Change
  const handleOtherNoteChange = (event: any) => {
    console.log("HighCostNote.handleOtherNoteChange")
    setOtherTextValue(event.target.value);
    // On Textinput Clear Button Click Reverting Back It To The Dropdown State and deleting the current selection.
    if (event.target.value === "" && event.nativeEvent.inputType === undefined && otherNoteSelected) {
      setDisplayText(null);
      setOtherTextValue("");
      setShowOtherInputText(false);
      setOtherNoteSelected(false);
      defaultFieldState.current = FieldState.Dropdown;
      // Delete The Note
      deleteNote();
    }
  };

  const handleEditButtonClick = () => {
    console.log("HighCostNote.handleEditButtonClick")
    if (otherNoteSelected) {
      setShowOtherInputText(true);
    }
  };

  // On Textinput Blur
  const handleTextInputBlur = (event: any) => {
    console.log("HighCostNote.handleTextInputBlur")
    if (event.target.value === "") {
      setDisplayText(null);
      setOtherTextValue("");
      // Delete The Note
      deleteNote();
      setOtherNoteSelected(false);
      defaultFieldState.current = FieldState.Dropdown;
    }

    if (event.target.value !== "") {
      setDisplayText(event.target.value);
      // Create Note
      if (displayText === null) {
        createNote(otherNoteHash + event.target.value);
      }

      // Update Note
      if (displayText !== null) {
        updateNote(otherNoteHash + event.target.value);
      }

      defaultFieldState.current = FieldState.Text;
    }

    setShowOtherInputText(false);
  };

  const deleteNote = () => {
    // to delete a note
    const body = {
      patientId: patient_id,
      expenseType: title === "Total Expense" ? "Total" : title,
      timePeriod: formatDisplayDate(refreshDate , "actualFormat" ,  "dataRefreshDateFormat"),
      description: null,
      update: true,
    };

    noteSaveApi(body);
  };

  const createNote = (value: string) => {
    // to create a note
    const body: any = {
      patientId: patient_id,
      expenseType: title === "Total Expense" ? "Total" : title,
      timePeriod: formatDisplayDate(refreshDate , "actualFormat" ,  "dataRefreshDateFormat"),
      description: value,
      update: false,
    };

    noteSaveApi(body);
  };

  const updateNote = (value: string) => {
    // to update a note
    const body: any = {
      patientId: patient_id,
      expenseType: title === "Total Expense" ? "Total" : title,
      timePeriod: formatDisplayDate(refreshDate , "actualFormat" ,  "dataRefreshDateFormat"),
      description: value,
      update: true,
    };

    noteSaveApi(body);
  };

  const noteSaveApi = (body: any) => {
    //to save the note changes
    setNoteLoadingState(true);
    HighCostPatientService.createNote(body).subscribe({
      next: (response: any) => {
        if(tableData && setTableData){
          const data = cloneDeep(tableData[body.expenseType]);
          const index = data.findIndex((row : any) => row.patient_id === body.patientId);
          if(index > -1){
            data[index].notes = body.description;
            setTableData(data , body.expenseType);
          }
        }
        if(expenseData && setExpenseData){
          const data = cloneDeep(expenseData);
          const expenseIndex = data.findIndex((e : any) => e.title?.includes(body.expenseType));
          if(expenseIndex > -1){
            data[expenseIndex].displayText = body.description;
            setExpenseData(data);
          }
        }
        setNoteLoadingState(false);
      },
      error: (error: any) => {
        setNoteLoadingState(false);
      },
    });
  };

  return (
    <div style={style} className={ classNames({
      "high-cost__note": true,
      "high-cost__note--tittle": title.includes('Total')
      }) }>
      <div className="high-cost__note_dot">{color && <Icon name="dot" colorOfBrand={color} height={size} width={size} />}</div>
      <div className="high-cost__note__content">
        <span className={ classNames({ "high-cost__note__content__label": true, "high-cost__note__content__label--bold": title.includes('Total')})}>
          { title }<span className="high-cost__note__content__rank">{rank ? ` (Rank: ${rank})` : (title.includes('Total') && expenseData[0]?.rank ? ` (Rank: ${expenseData[0]?.rank})` : "" )}</span>
        </span>
        <span className="high-cost__note__content__expense" style={{ fontSize: countFontSize, color: countColor }}>
          {displayCount}
        </span>
      </div>
      <div className={`${title.includes('Total') ? '' : 'high-cost__note__content__expense_input'}`}>
        {title.includes('Total') && <div className='high-cost__note__content__label--bold'>Reason</div>}
        {!showOtherInputText && (
          <div className={`flex ${displayCount === 'NA' ? 'disabled grey-color' : 'link-color'}`}>
            <div className="w-97per">
              <UpdateTextWithDropdown
                atrributeName="HighCostReasonLst"
                displayText={displayText}
                onDisplayTextChange={handleSelectChange}
                handleSelectChangeManually={true}
                loadingState={noteLoadingState}
                placeHolder={"Please Select"}
                onEditButtonClick={() => {
                  handleEditButtonClick();
                }}
                defaultFieldState={defaultFieldState.current}
                customDropdownData={dropdownData}
                disabled={displayCount === 'NA'}
                showEditText={true}
                showEditTextString="Add Reason"
                setNotesModal={setNotesModal}
                dropdownSize='middle'
              />
            </div>
          </div>
        )}

        {showOtherInputText && (
          <Input
            autoFocus
            allowClear
            addonBefore="Other:"
            defaultValue=""
            onBlur={(event) => {
              handleTextInputBlur(event);
            }}
            onPressEnter={(event) => {
              handleTextInputBlur(event);
            }}
            value={otherTextValue}
            onChange={(event: any) => {
              handleOtherNoteChange(event);
            }}
            maxLength={45}
          />
        )}
      </div>
    </div>
  );
};

const NotesModal = ({ patient_id, currrentTab , refreshDate , tableData , setTableData , setNotesModal}: any) => {
  const expenseTypeDropdown = useHighCostPatientStore((state) => state.expenseTypeDropdown);
  const setExpenseTypeDropdown = useHighCostPatientStore((state) => state.setExpenseTypeDropdown);
  const location: any = useLocation();

  useEffect(() => {
    /*to fetch the dropdown data for notes , apiCallNotNeeded will hep us to not call the api again when we come back from encounters data
    as we have already called the api and stored the response*/
    if (!location?.state?.apiCallNotNeeded || expenseTypeDropdown?.length <= 0) {
      appInsights.trackPageView({ name: "UpdateTextWithDropdown" });
      setLoading(true);
      memberDetailsService.fetchAttributeDropdown("HighCostReasonLst").subscribe({
        next: (response: any) => {
          setExpenseTypeDropdown(response.data.data.data);
          setLoading(false);
        },
        error: (error) => {
          setLoading(false);
          console.log(error);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expenseData = useHighCostPatientStore((state) => state.expenseData);
  const setExpenseData = useHighCostPatientStore((state) => state.setExpenseData);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // to fetch the data tot be shown in notes modal
    const getHighCostPatientNotes = async () => {
      if (patient_id && refreshDate) {
        setLoading(true);
        const payLoad = {
          patientId: patient_id,
          tab :  location.pathname === routeMap.memberProfile ? "Encounters Data" : "High Cost Report"
        };
        HighCostPatientService.getHighCostPatientsNotes(payLoad).subscribe({
          next: (response) => {
            let data = response?.data?.data;
            if(data && Object.keys(data).length > 0){
              setExpenseData([
                { title: "Total Expense", displayCount: data?.totalExpense || 'NA', count: parseNumber(data?.totalExpense?.split(" ")?.[1] || 0), displayText: data?.totalNote, rank : data?.totalCurrentRank},
                { title: "Part A", displayCount: data?.partAExpense || 'NA', count: (parseNumber(data?.partAExpense?.split(" ")?.[1] || 0)/parseNumber(data?.totalExpense?.split(" ")?.[1] || 0))*100, toolTipVal : data?.partAExpense || 0, color: "#807AF4", displayText: data?.partANote , rank : data?.partACurrentRank},
                { title: "Part B", displayCount: data?.partBExpense || 'NA', count: (parseNumber(data?.partBExpense?.split(" ")?.[1] || 0)/parseNumber(data?.totalExpense?.split(" ")?.[1] || 0))*100, toolTipVal : data?.partBExpense || 0, color: "#F1946F", displayText: data?.partBNote , rank : data?.partBCurrentRank},
                { title: "Part D", displayCount: data?.partDExpense || 'NA', count: (parseNumber(data?.partDExpense?.split(" ")?.[1] || 0)/parseNumber(data?.totalExpense?.split(" ")?.[1] || 0))*100, toolTipVal : data?.partDExpense || 0,color: "#7DC7F8", displayText: data?.partDNote , rank : data?.partDCurrentRank},
              ]);
            } else {
              setExpenseData(ExpenseData)
            }
            setLoading(false);
          },
          error: (error) => {
            setLoading(false);
          },
        });
      }
    }
    getHighCostPatientNotes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient_id, refreshDate]);

  return (
    <div className={"highCostPatient__Modal"}>
      <Spin spinning={loading} indicator={<HealthemSpinner />}>
        <HighCostNote
          title={"Total Expense"}
          displayCount={expenseData[0].displayCount}
          displayTextInitial={expenseData[0].displayText}
          patient_id={patient_id}
          dropdownData={expenseTypeDropdown}
          refreshDate={refreshDate}
          tableData={tableData}
          setTableData={setTableData}
          setNotesModal={setNotesModal}
          expenseData={expenseData}
          setExpenseData={setExpenseData}
        />
        <div className="notes-modal__progressBar">
          <ProgressBarChart
            config={expenseData.slice(1, expenseData.length).reduce((accumulator: any, currentValue: any) => accumulator + currentValue?.count, 0) > 0 ? expenseData.slice(1, expenseData.length) : [{ title: "default", count: 100, color: "#efeff0 " }]}
          />
        </div>
        {map(expenseData.slice(1, expenseData.length), (val) => (
          <HighCostNote
            {...val}
            size={currrentTab === val.title ? 16 : 8}
            displayTextInitial={val.displayText}
            countFontSize={16}
            countColor={"#091E42"}
            patient_id={patient_id}
            dropdownData={expenseTypeDropdown}
            refreshDate={refreshDate}
            tableData={tableData}
            setTableData={setTableData}
            setNotesModal={setNotesModal}
            expenseData={expenseData}
            setExpenseData={setExpenseData}
          />
        ))}
      </Spin>
    </div>
  );
};

export default NotesModal;
